import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import invariant from 'react-utils/invariant';
import { gate } from 'hub-http/gates';
import userInfo from 'hub-http/userInfo';
import Raven from 'raven-js';
import { RETINA_MULTIPLIER } from '../Constants';
import { AVATAR_SOURCES } from '../lib/AvatarSources';
import { getUnauthedHubSpotUserAvatarUrl } from '../lib/getUnauthedHubSpotUserAvatarUrl';
import { DEFAULT_CACHE_ENTRY } from '../constants/cacheConstants';
import { classifyLookups } from './classifyLookups';
import { dimensionToAvatarSize } from './dimensionToAvatarSize';
import { mapRpcAvatarSource } from './mapRpcAvatarSource';
import { callResolveAvatarsByIdentifier } from './avatarRpcClient';
import defaultFetchAvatars from './defaultFetchAvatars';
export const BASE_PATH = 'avatars/v1/';
export const BATCH_GET_PATH = 'avatars/batch';
const RESOLVER_RPC_GATE = gate('CRM:Records:UseAvatarsResolverRpc');
/**
 * Reduces the original lookups and the response into a Map of lookup => url.
 */
export function normalizeUpdates(lookups, response) {
  return lookups.reduce((updates, lookup) => {
    var _response$identifierN;
    const identifierName = lookup.getIdentifierName();
    const identifier = lookup.getIdentifier();
    if (!identifierName || !identifier) {
      return updates.set(lookup, DEFAULT_CACHE_ENTRY);
    }
    const responseData = (_response$identifierN = response[identifierName]) === null || _response$identifierN === void 0 ? void 0 : _response$identifierN[String(identifier)];
    if (responseData !== null && responseData !== void 0 && responseData.queued) {
      return updates;
    }
    return updates.set(lookup, {
      uri: (responseData === null || responseData === void 0 ? void 0 : responseData.uri) || null,
      source: (responseData === null || responseData === void 0 ? void 0 : responseData.source) || null
    });
  }, ImmutableMap());
}

/**
 * Reduces the original lookups and the response into a Set of lookups that are
 * queued and should be retried.
 */
export function normalizeRetries(lookups, response) {
  return lookups.reduce((retries, lookup) => {
    var _response$identifierN2;
    const identifierName = lookup.getIdentifierName();
    const identifier = lookup.getIdentifier();
    if (!identifierName || !identifier) {
      return retries;
    }
    const responseData = (_response$identifierN2 = response[identifierName]) === null || _response$identifierN2 === void 0 ? void 0 : _response$identifierN2[String(identifier)];
    if (!(responseData !== null && responseData !== void 0 && responseData.queued)) {
      return retries;
    }
    return retries.add(lookup);
  }, ImmutableSet());
}
export function normalizeError(lookups, response) {
  return {
    retry: ImmutableSet(),
    updates: normalizeUpdates(lookups, response)
  };
}
export function normalizeResponse(lookups, response) {
  return {
    retry: normalizeRetries(lookups, response),
    updates: normalizeUpdates(lookups, response)
  };
}

/**
 * Takes a list of lookups in transforms them to a JSON structure that the api
 * understands.
 */
export function toRequestData(lookups, dimension) {
  invariant(ImmutableSet.isSet(lookups), 'expected `lookups` to be a Set but got `%s`', lookups);
  invariant(typeof dimension === 'undefined' || typeof dimension === 'number', 'expected `dimension` to be undefined or a number but got `%s`', lookups);
  const requestDataWithoutDimensions = lookups.reduce((builder, lookup) => {
    const identifierName = lookup.getIdentifierName();
    if (!identifierName) {
      return builder;
    }
    const withoutKnown = builder.update(identifierName, maybeSet => {
      const identifier = lookup.getIdentifier();
      return typeof maybeSet === 'undefined' ? ImmutableSet.of(identifier) : maybeSet.add(identifier);
    });
    if (!lookup.isKnown()) {
      return withoutKnown;
    }
    const knownName = lookup.getKnownName();
    if (!knownName) {
      return withoutKnown;
    }
    return withoutKnown.update(knownName, maybeSet => {
      const knownObject = lookup.getKnownObject();
      return typeof maybeSet === 'undefined' ? ImmutableSet.of(knownObject) : maybeSet.add(knownObject);
    });
  }, ImmutableMap());
  if (typeof dimension === 'number') {
    const doubleDimensionForRetina = dimension * RETINA_MULTIPLIER;
    const sizeParams = {
      width: doubleDimensionForRetina,
      height: doubleDimensionForRetina
    };
    return Object.assign({}, requestDataWithoutDimensions.toJS(), {
      sizeParams
    });
  }
  return requestDataWithoutDimensions.toJS();
}
export function getUnauthedAvatarData(requestData) {
  if (!('hubSpotUserEmails' in requestData) || requestData.hubSpotUserEmails.length === 0) return {
    unauthedData: {},
    hasRemainingLookups: true
  };
  const unauthedData = requestData.hubSpotUserEmails.reduce((acc, email) => {
    var _requestData$sizePara;
    acc.hubSpotUserEmails[email] = {
      fromDefault: false,
      queued: false,
      source: AVATAR_SOURCES.HUBSETTINGS_HUBSPOT_USER,
      uri: getUnauthedHubSpotUserAvatarUrl({
        hubSpotUserEmail: email,
        dimension: (_requestData$sizePara = requestData.sizeParams) === null || _requestData$sizePara === void 0 ? void 0 : _requestData$sizePara.width
      })
    };
    return acc;
  }, {
    hubSpotUserEmails: {}
  });
  delete requestData.hubSpotUserEmails;
  const hasRemainingLookups = Object.keys(requestData).filter(key => key !== 'sizeParams').length > 0;
  return {
    unauthedData,
    hasRemainingLookups
  };
}
function buildAvatarCacheEntry(avatar, lookup) {
  if (!avatar) {
    return DEFAULT_CACHE_ENTRY;
  }
  return {
    uri: avatar.uri || null,
    source: mapRpcAvatarSource(avatar.source, lookup.type)
  };
}
function mergeMaps(...maps) {
  const merged = new Map();
  for (const map of maps) {
    for (const [key, value] of map) {
      merged.set(key, value);
    }
  }
  return merged;
}
function toImmutableCache(map) {
  return ImmutableMap(Array.from(map.entries()));
}
function resolveHubSpotUserEmails(entries, dimension) {
  return new Map(entries.map(({
    lookup,
    email
  }) => [lookup, {
    uri: getUnauthedHubSpotUserAvatarUrl({
      hubSpotUserEmail: email,
      dimension
    }),
    source: AVATAR_SOURCES.HUBSETTINGS_HUBSPOT_USER
  }]));
}
function resolveAvatarsByIdentifier(entries, avatarSize, trellisDefault) {
  if (entries.length === 0) {
    return Promise.resolve(new Map());
  }
  return callResolveAvatarsByIdentifier({
    identifiers: entries.map(entry => entry.identifier),
    size: avatarSize,
    trellisDefault
  }).then(response => {
    const avatarMap = response.avatarsByIdentifier;
    const results = new Map();
    for (const {
      lookup,
      responseKey
    } of entries) {
      const avatar = avatarMap[responseKey];
      results.set(lookup, buildAvatarCacheEntry(avatar, lookup));
    }
    return results;
  });
}
function buildDefaultFallbackMap(lookups) {
  return new Map(lookups.map(l => [l, DEFAULT_CACHE_ENTRY]));
}
function fetchAvatarsRpc(lookups, dimension, trellisDefault) {
  const classified = classifyLookups(new Set(lookups.toArray()));
  const avatarSize = dimensionToAvatarSize(dimension);
  const useTrellisDefault = trellisDefault !== null && trellisDefault !== void 0 ? trellisDefault : false;
  const hubSpotUserResults = resolveHubSpotUserEmails(classified.hubSpotUserEmails, dimension);
  const unclassifiedFallback = buildDefaultFallbackMap(classified.unclassified);
  return resolveAvatarsByIdentifier(classified.identifiers, avatarSize, useTrellisDefault).catch(err => {
    Raven.captureException(err instanceof Error ? err : new Error(`resolveAvatarsByIdentifier RPC failed: ${err}`), {
      extra: {
        identifierCount: classified.identifiers.length
      }
    });
    return buildDefaultFallbackMap(classified.identifiers.map(({
      lookup
    }) => lookup));
  }).then(identifierResults => ({
    retry: ImmutableSet(),
    updates: toImmutableCache(mergeMaps(unclassifiedFallback, hubSpotUserResults, identifierResults))
  }));
}

/**
 * Uses `fetcher` to retrieve and then normalize responses from the API.
 */
function fetchAvatarsApi(fetcher, portalId, lookups, dimension, trellisDefault) {
  invariant(typeof fetcher === 'function', 'expected `fetcher` to be a function but got `%s`', fetcher);
  invariant(typeof portalId === 'number' || typeof portalId === 'undefined', 'expected `portalId` to be a number or undefined but got `%s`', portalId);
  invariant(ImmutableSet.isSet(lookups), 'expected `lookups` to be a Set but got `%s`', lookups);
  invariant(typeof dimension === 'number' || typeof dimension === 'undefined', 'expected `dimension` to be a number or undefined but got `%s`', dimension);
  const queryParams = new URLSearchParams();
  if (typeof portalId !== 'undefined') {
    queryParams.set('portalId', String(portalId));
  }
  if (trellisDefault) {
    queryParams.set('trellisDefault', 'true');
  }
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  const requestData = toRequestData(lookups, dimension);
  const {
    unauthedData,
    hasRemainingLookups
  } = getUnauthedAvatarData(requestData);
  if (!hasRemainingLookups) {
    return Promise.resolve(normalizeResponse(lookups, unauthedData));
  }
  return fetcher(`${BASE_PATH}${BATCH_GET_PATH}${queryString}`, requestData).then(response => {
    if (typeof response !== 'object' || response === null) {
      throw new Error('Invalid response format');
    }
    return normalizeResponse(lookups, Object.assign({}, response, unauthedData));
  }).catch(() => {
    // If the request fails, we return an empty responses
    // for all lookups which results in the use of the fallback avatar icon or image
    return normalizeError(lookups, unauthedData);
  });
}
export function fetchAvatars(fetcher, portalId, lookups, dimension, trellisDefault) {
  // Custom fetchers replace the data source entirely, so bypass CHIRP and
  // use the REST path that honors the provided fetcher.
  if (fetcher !== defaultFetchAvatars) {
    return fetchAvatarsApi(fetcher, portalId, lookups, dimension, trellisDefault);
  }
  return userInfo().then(({
    gates
  }) => {
    if (gates.includes(RESOLVER_RPC_GATE)) {
      return fetchAvatarsRpc(lookups, dimension, trellisDefault);
    }
    return fetchAvatarsApi(fetcher, portalId, lookups, dimension, trellisDefault);
  }).catch(() => fetchAvatarsApi(fetcher, portalId, lookups, dimension, trellisDefault));
}